<script setup>
import { ref } from 'vue';

defineProps({
  title: { type: String, default: '' },
  subtitle: { type: String, default: '' },
  items: {
    type: Array,
    required: true,
    // Each item: { label, value, displayValue, percentage, color }
  },
  labelWidth: { type: String, default: '130px' },
});

const hoveredIdx = ref(null);

// Colors from tokens.css design system
const barBgStyle = color => {
  const map = {
    blue: 'linear-gradient(to right, rgba(46, 134, 193, 0.75), rgba(46, 134, 193, 0.95))', // --brand-blue #2E86C1
    cyan: 'linear-gradient(to right, rgba(93, 173, 226, 0.75), rgba(93, 173, 226, 0.95))', // --brand-cyan #5DADE2
    success:
      'linear-gradient(to right, rgba(16, 185, 129, 0.75), rgba(16, 185, 129, 0.95))', // --color-success #10B981
    warning:
      'linear-gradient(to right, rgba(245, 158, 11, 0.75), rgba(245, 158, 11, 0.95))', // --color-warning #F59E0B
    error:
      'linear-gradient(to right, rgba(239, 68, 68, 0.7), rgba(239, 68, 68, 0.92))', // --color-error #EF4444
    dark: 'linear-gradient(to right, rgba(27, 107, 147, 0.75), rgba(27, 107, 147, 0.95))', // --brand-dark #1B6B93
    'blue-light':
      'linear-gradient(to right, rgba(75, 163, 217, 0.75), rgba(75, 163, 217, 0.95))', // --brand-blue-light #4BA3D9
    'cyan-light':
      'linear-gradient(to right, rgba(133, 193, 233, 0.75), rgba(133, 193, 233, 0.95))', // --brand-cyan-light #85C1E9
  };
  return map[color] || map.blue;
};
</script>

<template>
  <div class="flex flex-col">
    <div
      v-if="title"
      class="text-[13px] font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
    >
      {{ title }}
      <span v-if="subtitle" class="font-normal text-n-slate-9 text-xs">{{
        subtitle
      }}</span>
    </div>
    <div
      class="bg-n-solid-2 border border-n-weak rounded-[10px] p-5 flex-1 overflow-y-auto max-h-[420px]"
    >
      <div
        v-for="(item, idx) in items"
        :key="idx"
        class="flex items-center gap-3 group cursor-default"
        :class="{ 'mb-2.5': idx < items.length - 1 }"
        @mouseenter="hoveredIdx = idx"
        @mouseleave="hoveredIdx = null"
      >
        <div
          class="text-xs flex-shrink-0 text-right transition-colors"
          :class="
            hoveredIdx === idx
              ? 'text-n-slate-12 font-medium'
              : 'text-n-slate-10'
          "
          :style="{ width: labelWidth }"
        >
          {{ item.label }}
        </div>
        <div class="flex-1 h-6 bg-n-solid-3 rounded overflow-hidden relative">
          <div
            class="h-full rounded transition-all duration-500"
            :class="hoveredIdx === idx ? 'opacity-100' : 'opacity-80'"
            :style="{
              width: `${Math.max(item.percentage || 0, 2)}%`,
              background: barBgStyle(item.color),
            }"
          />
          <!-- Hover tooltip inside bar track -->
          <div
            v-if="hoveredIdx === idx"
            class="absolute inset-0 flex items-center justify-end pr-2"
          >
            <span
              v-if="(item.percentage || 0) > 30"
              class="text-[11px] font-semibold text-white drop-shadow-sm"
            >
              {{ item.displayValue || item.value }}
            </span>
          </div>
        </div>
        <div
          class="text-xs font-semibold text-n-slate-12 w-20 flex-shrink-0 text-right transition-transform"
          :class="{ 'scale-110': hoveredIdx === idx }"
        >
          {{ item.displayValue || item.value }}
          <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
          <span v-if="item.percentage" class="text-n-slate-9 font-normal ml-1">
            {{ item.percentage }}%
          </span>
          <!-- eslint-enable @intlify/vue-i18n/no-raw-text -->
        </div>
      </div>
    </div>
  </div>
</template>
